import React, { useState, useEffect } from 'react';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { useFeatures } from '../../lib/features';
import { METHOD_FOR_TYPE, isInPerson } from '../../lib/planner';
import { activityFiles } from '../../lib/clientPage';
import { FilesEditor, uploadFiles } from './FilesEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Plus } from 'lucide-react';
import {
  activityTypesFor, CONTACT_METHODS, OUTCOMES, OPP_STAGES, TERMINAL_OUTCOMES,
  labelFor, addDaysISO, todayISO, apiError, ClientPicker,
} from './salesShared';

const pad2 = (n) => String(n).padStart(2, '0');
const hhmm = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const localDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// Now, rounded down to the quarter hour (a call just made).
const nowSlot = () => { const d = new Date(); d.setMinutes(Math.floor(d.getMinutes() / 15) * 15, 0, 0); return hhmm(d); };
// The next full hour (a meeting being booked).
const nextHour = () => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); return hhmm(d); };

// How long each kind of activity usually takes, until the user picks.
const DEFAULT_LENGTH = { call: 15, email: 15, visit: 60, proposal_shown: 60 };
const lengthFor = (type) => DEFAULT_LENGTH[type] || 30;
const LENGTHS = [15, 30, 60, 90, 120];
const lengthLabel = (m) => (m < 60 ? `${m}m` : `${m / 60}h`.replace('1.5h', '1½h'));

const emptyForm = (planned = false, startAt = null) => ({
  mode: planned ? 'planned' : 'done',  // done = already happened, planned = booked ahead
  occurred_on: startAt ? localDay(startAt) : todayISO(),
  time: startAt ? hhmm(startAt) : (planned ? nextHour() : nowSlot()),
  duration: planned ? 60 : 15,
  duration_touched: false,
  activity_type: planned ? 'visit' : 'call',
  contact_method: planned ? 'in_person' : 'phone',
  summary: '',
  notes: '',
  outcome: 'contacted',
  follow_up_date: '',
  related_opportunity_id: '',
  accompanied_by_rep_id: '',
});

// Logging the result of a planned meeting: start from what was booked.
const formFromActivity = (a) => {
  const when = new Date(a.occurred_at);
  return {
    ...emptyForm(false),
    occurred_on: localDay(when),
    time: hhmm(when),
    duration: a.duration_minutes || lengthFor(a.activity_type),
    duration_touched: true,
    activity_type: a.activity_type || 'visit',
    contact_method: a.contact_method || 'in_person',
    summary: a.summary || '',
    notes: a.notes || '',
    accompanied_by_rep_id: a.accompanied_by_rep_id || '',
  };
};

const QUICK_DATES = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'In a week', days: 7 },
  { label: 'In a month', days: 30 },
];

// Log one sales activity. Every activity needs a next follow-up date unless
// its outcome closes the conversation (Won, Lost, Not interested), so no
// client goes quiet by accident.
//
// client: { id, business_name } to log against (the picker is locked), or
//         null to let the user search for one.
// onSaveNext: when given, a "Save & next" button is shown (call lists).
// planned: open on "Schedule for later" (Sales Planner).
// startAt: a Date to prefill the day and time with.
// activity: a planned activity whose result is being logged; it is updated
//           instead of a new one being created.
const ActivityModal = ({ open, onClose, client = null, onSaved, onSaveNext, planned = false, startAt = null, activity = null }) => {
  const { salesOnly } = useFeatures();
  const { user } = useAuth();
  const [reps, setReps] = useState([]);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [opps, setOpps] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);   // [{ url, name }]
  const [uploading, setUploading] = useState(false);
  const clientId = client?.id || null;
  const activityId = activity?.id || null;
  const completing = !!activity;

  useEffect(() => {
    if (open) {
      if (activity) {
        setPicked({ id: activity.advertiser_id, business_name: activity.advertiser_name });
        setForm(formFromActivity(activity));
      } else {
        setPicked(client || null);
        setForm(emptyForm(planned, startAt));
      }
      setError('');
      setFiles(activity ? activityFiles(activity) : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the modal opens or what it is for changes
  }, [open, clientId, activityId, planned]);

  // Sales reps, for "Accompanied by".
  useEffect(() => {
    let alive = true;
    if (open) {
      api.get('/sales-reps').then(res => { if (alive) setReps(res || []); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [open]);

  const target = picked;
  const targetId = target?.id || null;

  // Open opportunities of the chosen client, to link the activity to one.
  useEffect(() => {
    let alive = true;
    setOpps([]);
    if (open && targetId) {
      api.get(`/sales/opportunities?advertiser_id=${encodeURIComponent(targetId)}&status=open`)
        .then(res => { if (alive) setOpps(res || []); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [open, targetId]);

  const isPlanned = form.mode === 'planned';
  const needsFollowUp = !isPlanned && !TERMINAL_OUTCOMES.includes(form.outcome);
  const today = todayISO();
  const dateOk = !!form.occurred_on && (isPlanned ? form.occurred_on >= today : form.occurred_on <= today);
  const timeOk = /^\d{2}:\d{2}$/.test(form.time || '');
  const valid = !!targetId && form.summary.trim() && (!needsFollowUp || form.follow_up_date) && !uploading && dateOk && timeOk;
  // The type fills in the contact method; it can still be changed.
  const setType = (v) => setForm(f => ({
    ...f,
    activity_type: v,
    contact_method: METHOD_FOR_TYPE[v] || f.contact_method,
    duration: f.duration_touched ? f.duration : lengthFor(v),
  }));
  // "Accompanied by": only for in-person, and never the rep already on it.
  const myEmail = (user?.email || '').trim().toLowerCase();
  const companionOptions = reps.filter(r => (completing
    ? r.id !== activity.sales_rep_id
    : (r.email || '').trim().toLowerCase() !== myEmail));
  const showCompanion = isInPerson(form.activity_type, form.contact_method) && companionOptions.length > 0;
  const setMode = (mode) => setForm(f => ({
    ...f,
    mode,
    occurred_on: mode === 'planned' ? (f.occurred_on < today ? today : f.occurred_on) : (f.occurred_on > today ? today : f.occurred_on),
    time: mode === 'planned' ? nextHour() : nowSlot(),
  }));

  const addFiles = async (chosen, onProgress) => {
    setUploading(true);
    setError('');
    const { uploaded, error: problem } = await uploadFiles(chosen, files.length, onProgress);
    setFiles(f => [...f, ...uploaded]);
    if (problem) setError(problem);
    setUploading(false);
  };

  const submit = async (next) => {
    if (!valid) return;
    setSubmitting(true);
    setError('');
    try {
      // The day and time picked, in the user's time zone.
      const occurredAt = new Date(`${form.occurred_on}T${form.time}:00`).toISOString();
      const body = {
        activity_type: form.activity_type,
        contact_method: form.contact_method,
        summary: form.summary.trim(),
        notes: form.notes || null,
        outcome: isPlanned ? null : form.outcome,
        follow_up_date: isPlanned ? null : (needsFollowUp ? form.follow_up_date : (form.follow_up_date || null)),
        related_opportunity_id: form.related_opportunity_id || null,
        attachments: files,
        occurred_at: occurredAt,
        duration_minutes: form.duration,
        status: isPlanned ? 'planned' : 'done',
        accompanied_by_rep_id: showCompanion ? form.accompanied_by_rep_id : '',
      };
      const saved = completing
        ? await api.put(`/sales/activities/${activity.id}`, body)
        : await api.post('/sales/activities', { ...body, advertiser_id: targetId, reminder_enabled: false });
      if (onSaved) await onSaved(saved, target);
      if (next && onSaveNext) onSaveNext(target);
      else onClose();
    } catch (e) {
      setError(apiError(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="activity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#3B82F6]" /> {completing ? 'Log the result' : (isPlanned ? 'Schedule a meeting' : 'Log Sales Activity')}
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            {isPlanned
              ? 'It shows in the Sales Planner. Log the result after it happens.'
              : 'The outcome becomes the client\'s status.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Client *</Label>
            <ClientPicker
              value={targetId}
              valueLabel={target?.business_name}
              onChange={(c) => setPicked(c)}
              disabled={!!client || completing}
              testid="activity-client-select"
            />
            {target?.phone && <p className="text-xs text-[#94A3B8]">Phone: <a href={`tel:${target.phone}`} className="text-[#3B82F6]">{target.phone}</a></p>}
          </div>

          {!completing && (
            <div className="grid grid-cols-2 border border-[#1E293B] rounded-md overflow-hidden" role="group" aria-label="When" data-testid="activity-mode">
              {[{ k: 'done', l: 'Already happened' }, { k: 'planned', l: 'Schedule for later' }].map(m => (
                <button
                  type="button" key={m.k}
                  aria-pressed={form.mode === m.k}
                  onClick={() => setMode(m.k)}
                  className={`h-10 text-sm ${form.mode === m.k ? 'bg-[#3B82F6] text-white font-medium' : 'text-[#94A3B8] hover:text-[#F8FAFC]'}`}
                  data-testid={`activity-mode-${m.k}`}
                >{m.l}</button>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">{isPlanned ? 'When is it? *' : 'When did it happen? *'}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                data-testid="activity-occurred-date"
                type="date"
                min={isPlanned ? today : undefined}
                max={isPlanned ? undefined : today}
                value={form.occurred_on}
                onChange={e => setForm(f => ({ ...f, occurred_on: e.target.value }))}
                className={`${inputCls} w-[170px]`}
              />
              <Input
                data-testid="activity-occurred-time"
                type="time"
                step="300"
                aria-label="Start time"
                value={form.time}
                onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className={`${inputCls} w-[130px]`}
              />
              {(isPlanned ? [{ label: 'Today', days: 0 }, { label: 'Tomorrow', days: 1 }] : [{ label: 'Today', days: 0 }, { label: 'Yesterday', days: -1 }]).map(q => (
                <button
                  type="button" key={q.days}
                  onClick={() => setForm(f => ({ ...f, occurred_on: addDaysISO(q.days) }))}
                  className={`text-xs px-2 py-1 rounded border ${form.occurred_on === addDaysISO(q.days) ? 'border-[#3B82F6] text-[#F8FAFC]' : 'border-[#1E293B] text-[#94A3B8]'} hover:text-[#F8FAFC] hover:border-[#3B82F6]`}
                  data-testid={`occurred-quick-${q.days}`}
                >{q.label}</button>
              ))}
              {!dateOk && <span className="text-xs text-amber-400" data-testid="occurred-date-hint">{isPlanned ? 'Pick today or a later day.' : 'Pick today or an earlier day.'}</span>}
              {!timeOk && <span className="text-xs text-amber-400">Pick a start time.</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Length" data-testid="activity-length">
              <span className="text-xs text-[#94A3B8] mr-1">Length</span>
              {LENGTHS.map(m => (
                <button
                  type="button" key={m}
                  aria-pressed={form.duration === m}
                  onClick={() => setForm(f => ({ ...f, duration: m, duration_touched: true }))}
                  className={`text-xs px-2.5 py-1 rounded border ${form.duration === m ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-[#F8FAFC]' : 'border-[#1E293B] text-[#94A3B8]'} hover:text-[#F8FAFC] hover:border-[#3B82F6]`}
                  data-testid={`activity-length-${m}`}
                >{lengthLabel(m)}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Activity Type *</Label>
              <Select value={form.activity_type} onValueChange={setType}>
                <SelectTrigger className={inputCls} data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {activityTypesFor(salesOnly).map(a => <SelectItem key={a.value} value={a.value} className="text-[#F8FAFC]">{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Contact Method</Label>
              <Select value={form.contact_method} onValueChange={v => setForm(f => ({ ...f, contact_method: v }))}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {CONTACT_METHODS.map(c => <SelectItem key={c.value} value={c.value} className="text-[#F8FAFC]">{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {showCompanion && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Accompanied by <span className="text-[#475569] text-xs">(optional, a second rep at this meeting)</span></Label>
              <Select value={form.accompanied_by_rep_id || 'none'} onValueChange={v => setForm(f => ({ ...f, accompanied_by_rep_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className={inputCls} data-testid="activity-companion-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  <SelectItem value="none" className="text-[#F8FAFC]">Nobody</SelectItem>
                  {companionOptions.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Summary * <span className="text-[#475569] text-xs">(short headline, shows in the timeline)</span></Label>
            <Input
              data-testid="activity-summary-input"
              value={form.summary}
              onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
              placeholder={salesOnly ? 'e.g. "Talked to the owner about a new storefront sign"' : 'e.g. "Talked to Baja Realty about a half-page ad in June"'}
              className={inputCls}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Detailed Notes</Label>
            <Textarea
              data-testid="activity-notes-input"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Internal notes — visible to your team, not the client."
              className={`${inputCls} min-h-[80px]`}
            />
          </div>

          {!isPlanned && (<>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Outcome / Status</Label>
              <Select value={form.outcome} onValueChange={v => setForm(f => ({ ...f, outcome: v }))}>
                <SelectTrigger className={inputCls} data-testid="activity-outcome-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {OUTCOMES.map(o => <SelectItem key={o.value} value={o.value} className="text-[#F8FAFC]">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">
                Next Follow-up Date {needsFollowUp ? '*' : <span className="text-[#475569] text-xs">(optional)</span>}
              </Label>
              <Input
                data-testid="activity-followup-date"
                type="date"
                value={form.follow_up_date}
                onChange={e => setForm(f => ({ ...f, follow_up_date: e.target.value }))}
                className={inputCls}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 -mt-2">
            {QUICK_DATES.map(q => (
              <button
                type="button" key={q.days}
                onClick={() => setForm(f => ({ ...f, follow_up_date: addDaysISO(q.days) }))}
                className="text-xs px-2 py-1 rounded border border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#3B82F6]"
                data-testid={`followup-quick-${q.days}`}
              >{q.label}</button>
            ))}
            {needsFollowUp && !form.follow_up_date && (
              <span className="text-xs text-amber-400" data-testid="followup-required-hint">A next step is required for this outcome.</span>
            )}
          </div>
          </>)}

          <div className="space-y-2" data-testid="activity-files">
            <Label className="text-[#94A3B8]">Files <span className="text-[#475569] text-xs">(optional — PDF, Word, Excel, PowerPoint, JPG or PNG, up to 20 MB each, or video up to 2GB)</span></Label>
            <FilesEditor files={files} onAdd={addFiles} onRemove={(i) => setFiles(f => f.filter((_, n) => n !== i))} busy={uploading} />
          </div>

          {opps.length > 0 && !isPlanned && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Related Opportunity (optional)</Label>
              <Select value={form.related_opportunity_id || 'none'} onValueChange={v => setForm(f => ({ ...f, related_opportunity_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  <SelectItem value="none" className="text-[#F8FAFC]">None</SelectItem>
                  {opps.map(o => <SelectItem key={o.id} value={o.id} className="text-[#F8FAFC]">{o.title} ({labelFor(OPP_STAGES, o.stage)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="activity-error">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]">Cancel</Button>
            {onSaveNext && (
              <Button type="button" onClick={() => submit(true)} disabled={submitting || !valid} variant="outline" className="border-[#3B82F6] bg-[#121212] text-[#3B82F6]" data-testid="activity-save-next-btn">
                Save &amp; next
              </Button>
            )}
            <Button type="button" onClick={() => submit(false)} disabled={submitting || !valid} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="activity-submit-btn">
              {submitting ? 'Saving…' : (completing ? 'Save result' : (isPlanned ? 'Add to planner' : 'Log Activity'))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityModal;
